import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { CalendarCheck2, Loader2, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import AreaLoginShell from "../../components/AreaLoginShell";
import { isNativeVisiteApp } from "../../lib/visite/bridge";

const API = process.env.REACT_APP_BACKEND_URL;
const MARK = "/visite/icons/visite-192.png";

export function canOpenVisite(user) {
  if (!user) return false;
  if (user.scope === "super_admin" || user.modules == null) return true;
  return Array.isArray(user.modules) && user.modules.includes("commerciale-visite");
}

export function readVisiteSession() {
  const token = localStorage.getItem("super_admin_token");
  if (!token) return null;
  try {
    return JSON.parse(localStorage.getItem("super_admin_user") || "{}");
  } catch {
    return {};
  }
}

function storeSession(data) {
  localStorage.setItem("super_admin_token", data.access_token);
  localStorage.setItem("super_admin_user", JSON.stringify({
    email: data.email,
    scope: data.scope,
    modules: data.modules || null,
    permissions: data.permissions || null,
    profile: data.profile || null,
    name: data.name || "",
  }));
}

export default function VisiteLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const session = readVisiteSession();

  if (session && canOpenVisite(session)) return <Navigate to="/visite" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/crm/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403 && data?.detail?.code === "super_admin_2fa_required") {
        // Super admin: niente accesso con sola password, si passa dal login Admin con 2FA.
        toast.info("Account Super Admin: completa l'accesso con la verifica in due passaggi.");
        navigate("/admin-login?next=/visite", { replace: true });
        return;
      }
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Credenziali non valide");
      if (!canOpenVisite(data)) {
        throw new Error("Questo account non ha accesso a Le mie visite.");
      }
      storeSession(data);
      toast.success("Accesso effettuato");
      navigate("/visite", { replace: true });
    } catch (err) {
      toast.error(err.message || "Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AreaLoginShell
      accent="emerald"
      currentArea="visite"
      hideAreaLinks={isNativeVisiteApp()}
      toggleClassName="fixed right-[max(0.75rem,env(safe-area-inset-right))] top-[max(0.75rem,env(safe-area-inset-top))] z-50"
      mainClassName="min-h-[100dvh] pt-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))]"
      title="Le mie visite, sul tablet"
      subtitle="Compila la visita in struttura, salva la bozza e collegala alla pipeline Affidya."
      logo={<img src={MARK} alt="Visite by Affidya" className="mb-3 h-12 w-12 rounded-2xl lg:mb-9" />}
      features={[
        { icon: CalendarCheck2, title: "Percorso guidato", text: "Cliente, intervista, servizi, note, esito e follow-up." },
        { icon: ShieldCheck, title: "Stesso accesso Admin", text: "Entri con le credenziali commerciali, senza il resto del pannello." },
      ]}
    >
      <div className="mb-6 text-center">
        <img src={MARK} alt="" className="mx-auto mb-3 h-12 w-12 rounded-2xl object-contain" />
        <h2 className="text-xl font-semibold tracking-tight text-slate-900">Accedi a Le mie visite</h2>
        <p className="mt-1 text-sm text-slate-500">Usa le stesse credenziali del pannello Admin</p>
      </div>
      <form className="space-y-4" onSubmit={handleSubmit}>
        <div>
          <Label htmlFor="visite-email">Email</Label>
          <Input id="visite-email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" />
        </div>
        <div>
          <Label htmlFor="visite-password">Password</Label>
          <Input id="visite-password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1" />
        </div>
        <Button type="submit" className="h-11 w-full bg-emerald-700 hover:bg-emerald-800" disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
          Entra
        </Button>
      </form>
    </AreaLoginShell>
  );
}
